import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  MagnifyingGlassIcon,
  BriefcaseIcon,
  ChatBubbleLeftRightIcon,
  UserCircleIcon,
  BellIcon,
  PlusCircleIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ClipboardDocumentListIcon,
  XMarkIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  LightBulbIcon,
  CheckCircleIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { logoutUser } from '../../store/slices/authSlice';
import LogoutModal from '../common/LogoutModal';
import { getProfileFieldsChecklist, hasCompletedRequiredProfile } from '../../utils/profileCompletion';
import Logo from '../common/Logo';
import BrandLogo from '../common/BrandLogo';
// [Subscription removed] useRestrictions and UpgradePopup removed — platform is fully free.
// import { useRestrictions } from '../../hooks/useRestrictions';
// import UpgradePopup from '../common/UpgradePopup';

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}



// ... (imports remain the same)

const Sidebar: React.FC<SidebarProps> = ({ isOpen = false, onClose }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);
  // [Subscription removed] CRM access check removed — CRM is accessible to all non-hirers.
  // const { checkRestriction } = useRestrictions();
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showProfileCompletionModal, setShowProfileCompletionModal] = useState(false);
  const createMenuRef = useRef<HTMLDivElement>(null);

  const isProfileIncomplete = 
    !!user && 
    !user.is_admin && 
    user.user_type !== 'hirer' && 
    !hasCompletedRequiredProfile({
      first_name: user.first_name,
      last_name: user.last_name,
      location: user.location,
      profile_pic: user.profile_pic || user.profile_pic_signed || user.profile_pic_medium_signed || user.profile_pic_thumb_signed,
      bio: user.bio,
      specializations: user.specializations || user.skills,
      phone: user.phone,
      website: user.website,
      experience: user.experience,
    });

  const navItems = user?.user_type === 'hirer'
    ? [
        { name: 'Explore', icon: MagnifyingGlassIcon, path: '/explore' },
        { name: 'My Requirements', icon: BriefcaseIcon, path: '/opportunities/my' },
        { name: 'Bookings', icon: UserGroupIcon, path: '/collaboration-requests' },
        { name: 'Memory Board', icon: ClipboardDocumentListIcon, path: '/memory-board' },
      ]
    : [
        { name: 'Explore', icon: MagnifyingGlassIcon, path: '/explore' },
        { name: 'Opportunities', icon: BriefcaseIcon, path: '/opportunities' },
        { name: 'Collaborations', icon: UserGroupIcon, path: '/collaboration-requests' },
        { name: 'Client CRM', icon: UserCircleIcon, path: '/crm' },
        { name: 'Memory Board', icon: ClipboardDocumentListIcon, path: '/memory-board' },
      ];

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (createMenuRef.current && !createMenuRef.current.contains(event.target as Node)) {
        setShowCreateMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const sidebarClasses = `
    fixed inset-y-0 left-0 z-[70] w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transform transition-transform duration-300 ease-in-out flex flex-col h-full
    ${isOpen ? 'translate-x-0' : '-translate-x-full'}
    md:translate-x-0 md:fixed md:h-screen
  `;

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      <aside className={sidebarClasses}>
        {/* Logo & Close Button */}
        <div 
          className="p-6 flex items-center justify-between"
          style={{ paddingTop: 'calc(max(var(--safe-area-inset-top, 0px), env(safe-area-inset-top, 0px)) + 1rem)' }}
        >
          <div className="flex items-center space-x-1">
            <Link to="/home">
              <BrandLogo iconSize="w-10 h-10" textSize="text-xl" />
            </Link>
          </div>
          {/* Close Button (Mobile Only) */}
          <button 
            onClick={onClose}
            className="md:hidden p-2 -mr-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-2 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.name}
              to={item.path}
              onClick={(e) => {
                if (isProfileIncomplete && item.path !== '/profile' && item.path !== '/profile/edit' && item.path !== '/settings' && item.path !== '/crm') {
                  e.preventDefault();
                  setShowProfileCompletionModal(true);
                  return;
                }
                onClose && onClose();
              }}
              className={({ isActive }) => {
                // Custom check for Profile to avoid highlighting on other users' profiles
                let active = isActive;
                if (item.path === '/profile' && active) {
                   const pathParts = location.pathname.split('/');
                   if (pathParts.length > 2 && pathParts[2] !== 'edit' && pathParts[2] !== 'complete') {
                      if (user && pathParts[2] !== user.id) {
                        active = false;
                      }
                   }
                }

                return `flex items-center px-4 py-3 rounded-xl text-[13px] font-medium transition-all duration-200 group outline-none focus:outline-none ${
                  active
                    ? 'bg-black dark:bg-white text-white dark:text-black shadow-md'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-black dark:hover:text-white'
                }`;
              }}
            >
              <item.icon className="w-6 h-6 mr-3" />
              {item.name}
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink
              to="/admin"
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center px-4 py-3 rounded-xl text-[13px] font-medium transition-all duration-200 group outline-none focus:outline-none ${
                  isActive
                    ? 'bg-red-600 text-white shadow-md'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-black dark:hover:text-white'
                }`
              }
            >
              <Cog6ToothIcon className="w-6 h-6 mr-3" />
              Admin Dashboard
            </NavLink>
          )}
        </nav>

        {/* Bottom Actions */}
        <div 
          className="p-4 border-t border-gray-100 dark:border-gray-800 space-y-1 mt-auto"
          style={{ paddingBottom: 'calc(max(var(--safe-area-inset-bottom, 0px), env(safe-area-inset-bottom, 0px)) + 1rem)' }}
        >
          {/* Create Button with Dropdown - Hide for Hirers */}
          {user?.user_type !== 'hirer' && (
            <div className="relative mb-2" ref={createMenuRef}>
              <button
                onClick={() => setShowCreateMenu(!showCreateMenu)}
                className="flex items-center justify-center w-full px-4 py-3 bg-gray-900 dark:bg-white text-white dark:text-black rounded-xl text-sm font-bold hover:bg-black dark:hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 outline-none focus:outline-none"
              >
                <PlusCircleIcon className="w-5 h-5 mr-2" />
                Create Post
              </button>

              {showCreateMenu && (
                <div className="absolute bottom-full left-0 w-full mb-2 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden z-50">
                  <button
                    onClick={() => {
                      navigate('/profile?action=add-portfolio');
                      setShowCreateMenu(false);
                      if (onClose) onClose();
                    }}
                    className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-black dark:hover:text-white transition-colors flex items-center"
                  >
                    <BriefcaseIcon className="w-4 h-4 mr-2" />
                    Add Portfolio Item
                  </button>
                  <button
                    onClick={() => {
                      navigate('/profile?action=create-collaboration');
                      setShowCreateMenu(false);
                      if (onClose) onClose();
                    }}
                    className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-black dark:hover:text-white transition-colors flex items-center border-t border-gray-50 dark:border-gray-700"
                  >
                    <UserGroupIcon className="w-4 h-4 mr-2" />
                    Create Collaboration
                  </button>
                </div>
              )}
            </div>
          )}

          {user?.user_type === 'hirer' && (
            <button
              onClick={() => {
                navigate('/opportunities/post');
                if (onClose) onClose();
              }}
              className="flex items-center justify-center w-full px-4 py-3 mb-2 bg-orange-600 text-white rounded-xl text-sm font-bold hover:bg-orange-600 transition-all shadow-lg hover:shadow-xl shadow-orange-500/20 transform hover:-translate-y-0.5 outline-none focus:outline-none"
            >
              <PlusCircleIcon className="w-5 h-5 mr-2" />
              Post New Requirement
            </button>
          )}

          <NavLink
            to="/settings"
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center px-4 py-3 rounded-xl text-[13px] font-medium transition-colors outline-none focus:outline-none ${
                isActive ? 'bg-gray-100 dark:bg-gray-800 text-black dark:text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`
            }
          >
            <Cog6ToothIcon className="w-6 h-6 mr-3" />
            Settings
          </NavLink>
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-4 py-3 rounded-xl text-[13px] font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors outline-none focus:outline-none"
          >
            <ArrowRightOnRectangleIcon className="w-6 h-6 mr-3" />
            Logout
          </button>
        </div>
      </aside>
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
      />

      {/* Profile Completion Modal triggered on tab clicks */}
      {showProfileCompletionModal && (
        <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 sm:p-8 max-w-md w-full text-center shadow-2xl border border-gray-100 dark:border-gray-800 relative overflow-hidden">
            <button
              onClick={() => setShowProfileCompletionModal(false)}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Close"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-orange-400 to-amber-500" />
            
            <div className="w-14 h-14 bg-orange-50 dark:bg-orange-950/30 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm border border-orange-100/50 dark:border-orange-900/30">
              <UserIcon className="w-7 h-7 text-orange-500" />
            </div>
            
            <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2 tracking-tight">
              Complete Profile Details
            </h3>
            
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
              Please complete your essential profile details to navigate platform tabs and unlock features:
            </p>

            {/* Checklist of missing details */}
            {user && (
              <div className="space-y-2 mb-6 text-left max-h-60 overflow-y-auto pr-1">
                {getProfileFieldsChecklist({
                  first_name: user.first_name,
                  last_name: user.last_name,
                  location: user.location,
                  bio: user.bio,
                  specializations: user.specializations || user.skills,
                  profile_pic: user.profile_pic || user.profile_pic_signed || user.profile_pic_medium_signed || user.profile_pic_thumb_signed,
                  phone: user.phone,
                  experience: user.experience,
                  website: user.website,
                }).map((item) => (
                  <div 
                    key={item.id} 
                    className={`flex items-center justify-between p-2.5 rounded-xl border text-xs font-medium transition-all ${
                      item.isFilled 
                        ? 'bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-100 dark:border-emerald-900/40 text-emerald-800 dark:text-emerald-300' 
                        : item.isRequired 
                          ? 'bg-orange-50/60 dark:bg-orange-950/20 border-orange-200 dark:border-orange-900/40 text-orange-900 dark:text-orange-200'
                          : 'bg-gray-50 dark:bg-gray-800/40 border-gray-100 dark:border-gray-700/60 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {item.isFilled ? (
                        <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      ) : item.isRequired ? (
                        <span className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />
                      ) : (
                        <span className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 flex-shrink-0" />
                      )}
                      {item.name}
                    </span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${
                      item.isFilled 
                        ? 'text-emerald-600 dark:text-emerald-400' 
                        : item.isRequired 
                          ? 'text-orange-600 dark:text-orange-400'
                          : 'text-gray-400 dark:text-gray-500'
                    }`}>
                      {item.isFilled ? 'Completed' : item.isRequired ? 'Required' : 'Optional'}
                    </span>
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setShowProfileCompletionModal(false);
                  if (onClose) onClose();
                  navigate('/profile/edit');
                }}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3.5 px-6 rounded-2xl transition-all shadow-lg shadow-orange-500/20 text-xs uppercase tracking-widest active:scale-95 duration-150"
              >
                Go to Profile Edit
              </button>
              <button
                onClick={() => setShowProfileCompletionModal(false)}
                className="w-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold py-3 px-6 rounded-2xl transition-all text-xs uppercase tracking-wider"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
